import React, { useState, useEffect, useRef } from "react";
import { FaWhatsapp, FaEnvelope, FaPhone, FaTimes, FaArrowRight, FaCommentDots } from "react-icons/fa";
import whatsapp from "../images/whatsapp.png";
import contact  from "../images/contact1.png";

/* ── Constants ─────────────────────────────────────── */
const CONTACT = {
  phone:    "+91 7318731222",
  email:    "info@skywisetechnologies.in",
  gmail:    "https://mail.google.com/mail/?view=cm&fs=1&to=info@skywisetechnologies.in",
  whatsapp: "https://wa.me/917318731222",
};

/* ── Component ─────────────────────────────────────── */
function Whatsapp() {
  const [activePopup, setActivePopup] = useState(null); // "whatsapp" | "contact" | null
  const modalRef   = useRef(null);
  const triggerRef = useRef(null); // remembers which button opened the modal

  /* close on Escape key */
  useEffect(() => {
    if (!activePopup) return;
    const handler = (e) => { if (e.key === "Escape") closePopup(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activePopup]);

  /* trap focus inside modal */
  useEffect(() => {
    if (activePopup && modalRef.current) {
      const focusable = modalRef.current.querySelectorAll(
        'a, button, input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length) focusable[0].focus();
    }
  }, [activePopup]);

  /* restore focus on close */
  const openPopup = (type, ref) => {
    triggerRef.current = ref;
    setActivePopup(type);
  };

  const closePopup = () => {
    setActivePopup(null);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const waButtonRef  = useRef(null);
  const ctaButtonRef = useRef(null);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root {
          --wp-green:      #25D366;
          --wp-green-dark: #1da851;
          --wp-blue:       #0072b1;
          --wp-blue-dark:  #005d93;
          --wp-shadow:     0 4px 16px rgba(0,0,0,0.22);
          --wp-radius:     14px;
          --font-display:  'Syne', sans-serif;
          --font-body:     'DM Sans', sans-serif;
        }

        /* ── Floating buttons ── */
        .wp-fab {
          position: fixed;
          right: 1.25rem;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: var(--wp-shadow);
          z-index: 999;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          outline: none;
          padding: 0;
          overflow: hidden;
        }
        .wp-fab:hover  { transform: scale(1.12); box-shadow: 0 6px 22px rgba(0,0,0,0.3); }
        .wp-fab:focus-visible {
          outline: 3px solid #fff;
          outline-offset: 2px;
        }
        .wp-fab img {
          width: 28px;
          height: 28px;
          object-fit: contain;
          display: block;
        }
        .wp-fab-whatsapp {
          bottom: 5.5rem;
          background: var(--wp-green);
        }
        .wp-fab-whatsapp:hover  { background: var(--wp-green-dark); }
        .wp-fab-contact {
          bottom: 1.5rem;
          background: var(--wp-blue);
        }
        .wp-fab-contact:hover   { background: var(--wp-blue-dark); }

        /* pulse ring on WhatsApp button */
        .wp-fab-whatsapp::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 3px solid var(--wp-green);
          animation: wp-ring 2.2s ease-out infinite;
          pointer-events: none;
        }
        @keyframes wp-ring {
          0%   { transform: scale(1);   opacity: 0.7; }
          70%  { transform: scale(1.7); opacity: 0;   }
          100% { transform: scale(1.7); opacity: 0;   }
        }

        /* ── Backdrop ── */
        .wp-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(10,20,40,0.55);
          backdrop-filter: blur(3px);
          z-index: 1100;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: wp-fade-in 0.2s ease;
        }
        @keyframes wp-fade-in { from { opacity: 0; } to { opacity: 1; } }

        /* ── Modal card ── */
        .wp-modal {
          position: relative;
          background: #fff;
          border-radius: var(--wp-radius);
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          width: 100%;
          max-width: 380px;
          overflow: hidden;
          font-family: var(--font-body);
          animation: wp-slide-up 0.25s ease;
        }
        @keyframes wp-slide-up {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }

        /* Modal header */
        .wp-modal-header {
          padding: 1.4rem 1.5rem 1.2rem;
          display: flex;
          align-items: center;
          gap: 0.9rem;
          border-bottom: 1px solid #e2e8f0;
        }
        .wp-modal-header.green { background: linear-gradient(135deg, #1db954 0%, #25d366 100%); }
        .wp-modal-header.blue  { background: linear-gradient(135deg, #005d93 0%, #0072b1 100%); }
        .wp-modal-icon {
          width: 44px; height: 44px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center;
          font-size: 1.3rem; color: #fff; flex-shrink: 0;
        }
        .wp-modal-header-text h4 {
          font-family: var(--font-display);
          font-size: 1rem; font-weight: 700;
          color: #fff; margin: 0 0 0.15rem; line-height: 1.2;
        }
        .wp-modal-header-text p {
          font-size: 0.78rem; color: rgba(255,255,255,0.8); margin: 0;
        }

        /* Close button */
        .wp-close {
          position: absolute;
          top: 0.85rem; right: 0.85rem;
          width: 30px; height: 30px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          color: #fff; font-size: 0.75rem;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: background 0.2s;
          outline: none;
        }
        .wp-close:hover { background: rgba(255,255,255,0.35); }
        .wp-close:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

        /* Modal body */
        .wp-modal-body { padding: 1.5rem; }

        /* WhatsApp CTA */
        .wp-wa-desc {
          font-size: 0.9rem; color: #475569; line-height: 1.65;
          margin-bottom: 1.25rem;
        }
        .wp-wa-btn {
          display: flex; align-items: center; justify-content: center;
          gap: 0.6rem; width: 100%; padding: 0.8rem 1rem;
          background: var(--wp-green); color: #fff;
          border: none; border-radius: 9px; cursor: pointer;
          font-family: var(--font-body); font-size: 0.95rem; font-weight: 700;
          text-decoration: none;
          transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
        }
        .wp-wa-btn:hover {
          background: var(--wp-green-dark); color: #fff;
          box-shadow: 0 6px 18px rgba(37,211,102,0.35);
          transform: translateY(-2px);
        }
        .wp-wa-btn svg { font-size: 1.1rem; }

        .wp-wa-note {
          font-size: 0.75rem; color: #94a3b8; text-align: center; margin-top: 0.85rem;
        }

        /* Contact info rows */
        .wp-contact-rows { display: flex; flex-direction: column; gap: 0.75rem; }
        .wp-contact-row {
          display: flex; align-items: center; gap: 0.85rem;
          padding: 0.9rem 1rem;
          border: 1.5px solid #e2e8f0; border-radius: 9px;
          text-decoration: none; color: inherit;
          transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
        }
        .wp-contact-row:hover {
          border-color: var(--wp-blue);
          box-shadow: 0 4px 14px rgba(0,114,177,0.12);
          transform: translateY(-2px);
        }
        .wp-contact-row-icon {
          width: 38px; height: 38px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.9rem;
        }
        .wp-contact-row-icon.email { background: rgba(0,123,255,0.08); color: #007bff; border: 1px solid rgba(0,123,255,0.18); }
        .wp-contact-row-icon.phone { background: rgba(0,114,177,0.08); color: var(--wp-blue); border: 1px solid rgba(0,114,177,0.18); }
        .wp-contact-row-label {
          font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.08em; color: #94a3b8; margin-bottom: 0.1rem;
        }
        .wp-contact-row-value {
          font-size: 0.88rem; font-weight: 600; color: #0f172a;
        }
        .wp-contact-row-arrow {
          margin-left: auto; color: #cbd5e1; font-size: 0.7rem;
          transition: color 0.2s, transform 0.2s;
        }
        .wp-contact-row:hover .wp-contact-row-arrow {
          color: var(--wp-blue); transform: translateX(3px);
        }

        /* Modal footer */
        .wp-modal-footer {
          padding: 0.85rem 1.5rem;
          border-top: 1px solid #e2e8f0;
          display: flex; align-items: center; justify-content: space-between;
        }
        .wp-footer-brand {
          font-size: 0.72rem; color: #94a3b8; font-weight: 500;
        }
        .wp-footer-brand strong { color: #475569; }
        .wp-footer-close-link {
          font-size: 0.78rem; color: #94a3b8; font-weight: 600;
          background: none; border: none; cursor: pointer;
          font-family: var(--font-body);
          transition: color 0.2s;
          text-decoration: underline;
          padding: 0;
        }
        .wp-footer-close-link:hover { color: #475569; }

        /* ── Mobile ── */
        @media (max-width: 400px) {
          .wp-fab { width: 44px; height: 44px; }
          .wp-fab img { width: 24px; height: 24px; }
        }
      `}</style>

      {/* ── Floating WhatsApp button ── */}
      <button
        ref={waButtonRef}
        className="wp-fab wp-fab-whatsapp"
        onClick={() => openPopup("whatsapp", waButtonRef.current)}
        aria-label="Chat with us on WhatsApp"
        aria-haspopup="dialog"
        title="Chat on WhatsApp"
      >
        <img src={whatsapp} alt="" aria-hidden="true" />
      </button>

      {/* ── Floating Contact button ── */}
      <button
        ref={ctaButtonRef}
        className="wp-fab wp-fab-contact"
        onClick={() => openPopup("contact", ctaButtonRef.current)}
        aria-label="Open contact information"
        aria-haspopup="dialog"
        title="Contact us"
      >
        <img src={contact} alt="" aria-hidden="true" />
      </button>

      {/* ── Modal ── */}
      {activePopup && (
        <div
          className="wp-backdrop"
          onClick={closePopup}
          aria-hidden="true"
        >
          <div
            ref={modalRef}
            className="wp-modal"
            role="dialog"
            aria-modal="true"
            aria-label={activePopup === "whatsapp" ? "Chat with us on WhatsApp" : "Contact us"}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`wp-modal-header ${activePopup === "whatsapp" ? "green" : "blue"}`}>
              <div className="wp-modal-icon">
                {activePopup === "whatsapp" ? <FaWhatsapp /> : <FaCommentDots />}
              </div>
              <div className="wp-modal-header-text">
                <h4>
                  {activePopup === "whatsapp" ? "Chat on WhatsApp" : "Get in Touch"}
                </h4>
                <p>
                  {activePopup === "whatsapp"
                    ? "Typically replies within a few minutes"
                    : "Reach us directly — we're here to help"}
                </p>
              </div>
            </div>

            {/* Close button */}
            <button
              className="wp-close"
              onClick={closePopup}
              aria-label="Close dialog"
            >
              <FaTimes />
            </button>

            {/* Body */}
            <div className="wp-modal-body">
              {activePopup === "whatsapp" ? (
                <>
                  <p className="wp-wa-desc">
                    Have a question? Send us a message on WhatsApp and our team
                    will respond as quickly as possible.
                  </p>
                  <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wp-wa-btn"
                  >
                    <FaWhatsapp />
                    Start WhatsApp Chat
                    <FaArrowRight style={{ fontSize: "0.75rem" }} />
                  </a>
                  <p className="wp-wa-note">
                    Opens WhatsApp — available on mobile &amp; desktop
                  </p>
                </>
              ) : (
                <div className="wp-contact-rows">
                  <a
                    href={CONTACT.gmail}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wp-contact-row"
                  >
                    <div className="wp-contact-row-icon email">
                      <FaEnvelope />
                    </div>
                    <div>
                      <div className="wp-contact-row-label">Email Us</div>
                      <div className="wp-contact-row-value">{CONTACT.email}</div>
                    </div>
                    <FaArrowRight className="wp-contact-row-arrow" />
                  </a>

                  <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wp-contact-row"
                  >
                    <div className="wp-contact-row-icon phone">
                      <FaPhone />
                    </div>
                    <div>
                      <div className="wp-contact-row-label">Call Us</div>
                      <div className="wp-contact-row-value">{CONTACT.phone}</div>
                    </div>
                    <FaArrowRight className="wp-contact-row-arrow" />
                  </a>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="wp-modal-footer">
              <span className="wp-footer-brand">
                <strong>Skywise Technologies</strong> · Support
              </span>
              <button className="wp-footer-close-link" onClick={closePopup}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Whatsapp;